import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Rating from 'yoda-core-components/lib/components/Rating/rating';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import { detectisIE } from 'yoda-core-components/lib/helpers/Utils/Utils';
import SiteComponent from 'yoda-site-components/lib/components/SiteComponent/SiteComponent';
import Pricing from 'yoda-site-components/lib/components/Pricing/Pricing';
import PricingHelper from 'yoda-site-components/lib/components/Pricing/PricingHelper';
import ErrorBoundary from 'yoda-site-components/lib/components/ErrorBoundary/ErrorBoundary';
import List from 'yoda-core-components/lib/components/List/List';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Success from 'yoda-core-components/lib/assets/svg/Success.svg';
import ArrowRight from 'yoda-core-components/lib/assets/svg/ArrowRight.svg';
import ArrowLeft from 'yoda-core-components/lib/assets/svg/ArrowLeft.svg';
import Link from 'yoda-site-components/lib/components/Link/Link';
import some from 'lodash/some';
import get from 'lodash/get';
import selectProductType from 'yoda-site-components/lib/selectors/productDetails/selectProductType';
import { getCartDetailsAction } from 'yoda-checkout-components/lib/actions/AddToCartInterstitialAction';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { triggerNavigationClick } from 'yoda-site-components/lib/actions/AnalyticsAction';
import getProductCollectionPricing from '../../actions/ProductCollectionPricingAction';
import SectionTitle from '../PDPSectionTitle/PDPSectionTitle';
import * as styles from './ProductCollection.css';
import getProductCollectionItems from '../../selectors/ProductCollectionSelector';
import { shopTheCollectionLoadSuccess } from '../../actions/AnalyticsAction';

const cx = classNames.bind(styles);

const add = (param1, param2) => param1 + param2;
const subtract = (param1, param2) => param1 - param2;

/* istanbul ignore next */
const calculateScrollPixel = ({
    productPanesDisplayed,
    totalProductPanes,
    scrollPixels,
    scrolledPanes,
    productPaneWidth,
    scrollToRight,
}) => {
    const operator = scrollToRight ? subtract : add;

    // Calculate remaining(hidden panes) product panes in container to display
    const remainingProductsPanes = scrollToRight
        ? totalProductPanes - productPanesDisplayed - scrolledPanes
        : scrolledPanes;

    // Calculate product panes to show. It depends on the remaining panes and container size.
    const numberOfPanesToMove =
        remainingProductsPanes >= productPanesDisplayed
            ? productPanesDisplayed
            : remainingProductsPanes;

    // Calculate next scroll position
    const scrollX = numberOfPanesToMove * productPaneWidth;
    const scrollContentBy = operator(scrollPixels, scrollX);

    // Calculate the total number of panes which are scrolled
    const productPanesScrolled = scrollToRight
        ? scrolledPanes + numberOfPanesToMove
        : scrolledPanes - numberOfPanesToMove;

    // Calculate the total panes which are displaced from its original position
    const migratedProductPanes = scrollToRight
        ? add(productPanesScrolled, productPanesDisplayed)
        : productPanesScrolled;

    return {
        scrollContentBy,
        migratedProductPanes,
        productPanesScrolled,
    };
};

export class ProductCollection extends SiteComponent {
    static propTypes = {
        automationId: PropTypes.string,
        productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        enrId: PropTypes.string,
        bagItemCount: PropTypes.number,
        cartDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        selectProductType: PropTypes.oneOfType([PropTypes.object]),
        productCollectionPrice: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        productCollectionItems: PropTypes.oneOfType([PropTypes.object]),
        removeLinkPrefix: PropTypes.bool,
    };

    static defaultProps = {
        automationId: '',
        productDetails: {},
        enrId: null,
        cartDetails: {},
        actions: {},
        selectProductType: {},
        bagItemCount: 0,
        productCollectionPrice: [],
        productCollectionItems: {},
        removeLinkPrefix: false,
    };

    constructor(props) {
        super(props);
        this.refWrapper = this.refWrapper.bind(this);
        this.refProductPane = this.refProductPane.bind(this);
        this.renderPriceDetails = this.renderPriceDetails.bind(this);
    }

    componentDidMount() {
        if (this.props.bagItemCount > 0) {
            this.props.actions.getCartDetailsAction({ showLoader: false });
        }
    }

    componentDidUpdate() {
        const digitalData = window && window.digitalData;
        if (!this.state.analyticsActionTriggered && digitalData)
            this.triggerAnalyticsAction(digitalData);
    }

    hydrateOnMount() {
        const {
            productCollectionItems: { collectionItems },
            deviceType,
        } = this.props;
        const collections = get(this.props.productDetails, 'collection', []);

        this.setState({
            displayArrows: collectionItems.length > 8,
        });
        /* istanbul ignore else */
        if (collections) {
            this.props.actions.getProductCollectionPricing(collectionItems);
        }
    }

    state = {
        listStyle: {},
        disableLeftArrow: true,
        disableRightArrow: false,
        displayArrows: false,
        analyticsActionTriggered: false,
    };

    scrollStatus = {
        scrollPixels: 0,
        scrolledPanes: 0,
    };

    triggerAnalyticsAction = (digitalData) => {
        this.props.actions.shopTheCollectionLoadSuccess(digitalData);
        this.setState({
            analyticsActionTriggered: true,
        });
    };

    getPaginationButtons = () => {
        const { disableLeftArrow, disableRightArrow } = this.state;
        const leftArrowClass = cx({ iconClass: true, disabledArrow: disableLeftArrow });
        const rightArrowClass = cx({ iconClass: true, disabledArrow: disableRightArrow });
        const leftArrowStyle = dt([
            'absolute',
            'w-10',
            'h-10',
            'rounded-half',
            'bg-white',
            'bg-opacity-90',
            'shadow-elevation1',
            'z-[4]',
            'm-auto',
            'cursor-pointer',
            'bottom-0',
            'top-0',
            'left-5',
            'ml-6',
            'disabled:hidden',
        ]);
        const rightArrowStyle = dt([
            'absolute',
            'w-10',
            'h-10',
            'rounded-half',
            'bg-white',
            'bg-opacity-90',
            'shadow-elevation1',
            'z-[4]',
            'm-auto',
            'cursor-pointer',
            'bottom-0',
            'top-0',
            'right-0',
            'mr-6',
            'disabled:hidden',
        ]);
        return (
            <div className={`${cx('arrowContainer')} ${dt(['smMd:hidden'])}`}>
                <button
                    className={cx('arrow', 'leftArrow', leftArrowStyle)}
                    disabled={disableLeftArrow}
                    onClick={() => this.scrollCollection(false)}
                >
                    <Icon
                        iconType="svg"
                        className="arrow-icon"
                        width="19.5px"
                        height="29px"
                        viewBox="0 0 20 20"
                        pathClassName={leftArrowClass}
                    >
                        <ArrowLeft />
                    </Icon>
                </button>
                <button
                    className={cx('arrow', 'rightArrow', rightArrowStyle)}
                    disabled={disableRightArrow}
                    onClick={() => this.scrollCollection(true)}
                >
                    <Icon
                        iconType="svg"
                        className="arrow-icon"
                        width="19.5px"
                        height="29px"
                        viewBox="0 0 20 20"
                        pathClassName={rightArrowClass}
                    >
                        <ArrowRight />
                    </Icon>
                </button>
            </div>
        );
    };

    scrollCollection = (scrollToRight) => {
        const wrapperWidth = this.wrapper.getBoundingClientRect().width;
        const productPaneWidth = this.productPane.getBoundingClientRect().width + 10;
        const { scrollPixels, scrolledPanes } = this.scrollStatus;
        const {
            productCollectionItems: { selectedCollection },
            deviceType,
        } = this.props;

        // Calculate number of products displayed in the container
        const totalProductPanes = selectedCollection.products.length + 1;
        const result = wrapperWidth / productPaneWidth;
        const productPanesDisplayed = Math.floor(result);
        const mountedCartrdigeInfo = {
            productPanesDisplayed,
            totalProductPanes,
            scrollPixels,
            scrolledPanes,
            productPaneWidth,
            scrollToRight,
        };

        const {
            scrollContentBy,
            productPanesScrolled,
            migratedProductPanes,
        } = calculateScrollPixel(mountedCartrdigeInfo);

        this.scrollStatus.scrollPixels = scrollContentBy;
        this.scrollStatus.scrolledPanes = productPanesScrolled;

        this.setState({
            disableLeftArrow: migratedProductPanes === 0,
            disableRightArrow: migratedProductPanes === totalProductPanes,
            listStyle: {
                transform: `translate(${scrollContentBy}px)`,
            },
        });
    };

    refWrapper(el) {
        this.wrapper = el;
    }

    refProductPane(el) {
        this.productPane = el;
    }

    checkItemInCart = (collectionItem) => {
        const bagDetails = this.props.cartDetails.bag;
        /* istanbul ignore next */
        if (bagDetails) {
            return some(bagDetails.items, (item) => item.product.id === collectionItem.id);
        }
        return false;
    };

    shopTheCollectionLinkOnClick = (selectedCollectionId) => {
        this.props.actions.triggerNavigationClick({
            linkName: `middle:shop the collection:shopTheCollectionID:${selectedCollectionId}`,
            isReload: false,
        });
    };

    renderPriceDetails(collectionId, productCollectionPrice) {
        const pricingConfig = {
            disableFPACAPI: false,
        };

        let priceDetailsHTML = null;
        /* istanbul ignore next */
        productCollectionPrice.forEach((data) => {
            /* istanbul ignore next */
            if (data.id === collectionId && data.priceDetails) {
                const pricingDetails = PricingHelper(data.priceDetails);
                priceDetailsHTML = (
                    <div
                        className={`${cx('priceWrapper')} ${dt([
                            'text-left',
                            'absolute',
                            'bottom-1',
                            'mb-1',
                        ])}`}
                    >
                        <ErrorBoundary>
                            <Pricing
                                pricingDetails={pricingDetails}
                                pricingConfig={pricingConfig}
                                priceWrapperClass={`${cx('priceWrapperClass')} ${dt([
                                    'whitespace-normal',
                                    'leading-[1.2]',
                                ])}`}
                                priceClass={`${cx('priceClass')} ${dt(['text-base'])}`}
                                customMarketingLabel={`${cx('customMarketingLabel')} ${dt([
                                    'text-[11px]',
                                ])}`}
                                priceLabelClass={cx({
                                    clearance:
                                        pricingDetails.type ===
                                        `${dt([
                                            'block',
                                            'text-sm',
                                            'leading-[1.29]',
                                            'mt-0.5',
                                            'mx-0',
                                            'ml-2.5',
                                        ])}`,
                                })}
                            />
                        </ErrorBoundary>
                    </div>
                );
            }
        });
        return priceDetailsHTML;
    }

    productCollectionRenderer = (dataItem, index) => {
        const {
            selectProductType: { templateType },
            location,
            deviceType,
            productCollectionItems: { selectedCollection },
        } = this.props;
        /* istanbul ignore next */
        const query = makeQuery(location.search);
        const productTemplateType = query.pTmplType ? query.pTmplType : templateType;
        const productName = dataItem.image
            ? dataItem.image.altText
                  .replace(/[^a-zA-Z0-9.]/g, '-')
                  .replace(/-{2,}/g, '-')
                  .replace(/[.]/g, '')
                  .toLowerCase()
            : '';
        const linkPath =
            index === 0
                ? `/p/${productName}/${dataItem.id}?pTmplType=${productTemplateType}`
                : `/p/${productName}/${dataItem.id}?pTmplType=${productTemplateType}&enrId=${selectedCollection.id}`;
        /* istanbul ignore next */
        const rating =
            dataItem.valuation && dataItem.valuation.rating ? dataItem.valuation.rating : null;
        const prodEngageAnalyticsData = `middle:shop the collection:shopTheCollectionID:${selectedCollection.id}:shopTheCollectionPPID:${dataItem.id}`;
        return (
            <a
                href={linkPath}
                onClick={() =>
                    this.props.actions.triggerNavigationClick({
                        linkName: prodEngageAnalyticsData,
                        isReload: false,
                    })
                }
            >
                <div
                    className={`${cx(
                        'list-li',
                        `${
                            index === 0
                                ? `${cx('select')} ${dt([
                                      'border-2',
                                      'border-solid',
                                      'border-black',
                                  ])}`
                                : ''
                        }`
                    )} ${dt([
                        'border',
                        'border-solid',
                        'border-gray-25',
                        'rounded-lg',
                        'relative',
                        'pb-[100%]',
                        'h-full',
                        'w-full',
                    ])}`}
                    data-automation-id={`shop_collection_li${index === 0 ? '_selected' : ''}`}
                    ref={this.refProductPane}
                >
                    {dataItem.image && (
                        <img
                            className={dt([
                                'rounded-lg',
                                'absolute',
                                'top-0',
                                'left-0',
                                'right-0',
                                'bottom-0',
                                'w-full',
                                'h-full',
                            ])}
                            alt={dataItem.image.altText}
                            src={dataItem.image.url}
                        />
                    )}
                    {this.checkItemInCart(dataItem) ? (
                        <span
                            className={`${cx('checkIcon')} ${dt([
                                'rounded-full',
                                'absolute',
                                'top-1/2',
                                'right-[46%]',
                                'w-[41px]',
                                'h-[41px]',
                                'overflow-hidden',
                                '-mt-[20.5px]',
                                '-mr-[20.5px]',
                                'mb-0',
                                'ml-0',
                                'bg-white',
                            ])}`}
                        >
                            <Icon
                                iconType="svg"
                                pathClassName={cx('iconPath')}
                                className={`${cx('svg-icon')} ${dt([
                                    'absolute',
                                    '-top-3',
                                    '-left-3',
                                ])}`}
                                width="65"
                                height="65"
                                viewBox="0 0 65 65"
                            >
                                <Success />
                            </Icon>
                        </span>
                    ) : null}
                    {deviceType.isDesktop ? (
                        <div
                            className={`${cx('collectionDetails')} ${dt([
                                'absolute',
                                'lg:w-[152px]',
                                'lg:h-[154px]',
                                'smMd:w-[133px]',
                                'smMd:h-[133px]',
                                'p-2',
                                'z-[1]',
                                'opacity-90',
                                '-left-0.5',
                                index === 0 ? '-top-px' : 'top-0',
                                'hidden',
                                'rounded-lg',
                                'bg-white',
                                'group-hover:inline',
                                'overflow-hidden',
                            ])}`}
                        >
                            {dataItem.image && (
                                <p
                                    className={`${cx('collectionTitle')} ${dt([
                                        'text-xsmall',
                                        'text-left',
                                        'text-black',
                                        'font-open-sans-regular',
                                    ])}`}
                                >
                                    {dataItem.image.displayText}
                                </p>
                            )}
                            {rating ? (
                                <div
                                    className={`${cx('collectionRatingContainer')} ${dt([
                                        'py-0',
                                        'leading-[0]',
                                        'text-left',
                                    ])}`}
                                >
                                    <Rating
                                        averageRating={rating}
                                        totalRatings={5}
                                        ratingIconSize="small"
                                        wrapperClassName={`${cx('rating')} ${dt([
                                            'overflow-visible',
                                        ])}`}
                                    />
                                </div>
                            ) : null}
                            {this.renderPriceDetails(
                                dataItem.id,
                                this.props.productCollectionPrice
                            )}
                        </div>
                    ) : null}
                </div>
            </a>
        );
    };

    renderShopTheCollection = () => {
        const {
            deviceType,
            productCollectionItems: { selectedCollection = {} },
            removeLinkPrefix,
        } = this.props;
        const url = get(selectedCollection, 'link.url', '');
        const selectedCollectionId = get(selectedCollection, 'id', '');
        const blockText = 'Shop the Collection';

        if (url) {
            return (
                <>
                    {removeLinkPrefix ? (
                        <Link
                            className={`${cx({
                                shopCollectionLink: true,
                            })} ${dt([
                                'lg:text-xLg',
                                'lg:leading-8',
                                'smMd:text-lg',
                                'smMd:leading-7',
                                'font-open-sans-semibold',
                                'text-black',
                                'underline',
                                'mb-3',
                                'block',
                                'text-center',
                            ])}`}
                            href={url}
                            onClick={() => this.shopTheCollectionLinkOnClick(selectedCollectionId)}
                            removeLinkPrefix
                        >
                            {blockText}
                        </Link>
                    ) : (
                        <a
                            className={`${cx({
                                shopCollectionLink: true,
                            })} ${dt([
                                'text-xLg',
                                'leading-8',
                                'font-open-sans-semibold',
                                'text-black',
                                'underline',
                                'mb-3',
                                'block',
                                'text-center',
                            ])}`}
                            href={url}
                            onClick={() => this.shopTheCollectionLinkOnClick(selectedCollectionId)}
                        >
                            {blockText}
                        </a>
                    )}
                </>
            );
        }

        return <SectionTitle>{blockText}</SectionTitle>;
    };

    render() {
        const {
            deviceType,
            productDetails,
            productCollectionItems: { collectionItems },
        } = this.props;
        const productCollection = productDetails.collection ? productDetails.collection : [];
        const listBodyStyle = deviceType.isDesktop
            ? { overflow: 'hidden' }
            : { overflowY: 'hidden' };

        let classforMd = '';
        if (deviceType.isTablet) {
            classforMd = collectionItems.length > 5 ? 'md:!justify-start' : 'md:!justify-center';
        }
        let classforSm = '';
        if (deviceType.isMobile) {
            classforSm = collectionItems.length > 2 ? 'sm:!justify-start' : 'sm:!justify-center';
        }

        return detectisIE() || productCollection.length ? (
            <section className={`${cx('shopCollectionContainer')} ${dt(['bg-white'])}`}>
                <div
                    className={`${cx('wrapper')} ${dt([
                        'w-full',
                        'pt-0',
                        'lg:pb-6',
                        'smOnly:pb-5',
                        'mdOnly:pb-3',
                        'px-0',
                        'relative',
                        'flex',
                        'flex-col',
                        'items-center',
                        'justify-center',
                        'col12Block',
                    ])}`}
                >
                    <div className={dt(['flex', 'justify-center'])}>
                        {this.renderShopTheCollection()}
                    </div>
                    <div
                        className={`${cx('row', 'listContainer')} ${dt([
                            'm-0',
                            'relative',
                            'align-middle',
                            'col12Block',
                            'smMd:w-[calc(100%+48px)]',
                            'smMd:-mx-6',
                            'p-0',
                        ])}`}
                        ref={this.refWrapper}
                    >
                        {deviceType.isDesktop &&
                            this.state.displayArrows &&
                            this.getPaginationButtons()}
                        <ErrorBoundary>
                            <List
                                datasource={collectionItems}
                                listStyleClass={`${cx('listStyleClass')} ${dt([
                                    'flex',
                                    'transition-transform',
                                    'duration-[400]',
                                    'ease-in-out',
                                    'smMd:pb-3',
                                    classforMd,
                                    classforSm,
                                    !this.state.displayArrows
                                        ? 'lg:justify-center'
                                        : 'lg:justify-start',
                                ])}`}
                                itemStyleClass={`${cx('itemStyleClass')} ${dt([
                                    'group',
                                    'flex-grow-0',
                                    'flex-shrink-0',
                                    'p-0.5',
                                    'border',
                                    'border-solid',
                                    'border-white',
                                    'relative',
                                    'smMd:w-[132px]',
                                    'lg:w-[154px]',
                                    'smMd:first:!ml-6',
                                ])}`}
                                listBodyStyle={listBodyStyle}
                                childRenderer={this.productCollectionRenderer}
                                automationId="shop-collection-list"
                                listStyle={this.state.listStyle}
                                spacing="None"
                            />
                        </ErrorBoundary>
                    </div>
                </div>
            </section>
        ) : null;
    }
}

const mapStateToProps = (state) => ({
    deviceType: state.context ? state.context.deviceType : {},
    cartDetails: state.addToCartInterstitialDetails,
    selectProductType: selectProductType(false)(state),
    bagItemCount: state.bagItemCount,
    productCollectionPrice: state.productCollection,
    productCollectionItems: getProductCollectionItems(state),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            getCartDetailsAction,
            getProductCollectionPricing,
            shopTheCollectionLoadSuccess,
            triggerNavigationClick,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductCollection);
